'use client';

import { cn } from '@/lib/utils';

const PALETTE = [
  'oklch(0.64 0.22 272)',
  'oklch(0.62 0.24 285)',
  'oklch(0.72 0.14 200)',
  'oklch(0.75 0.18 60)',
  'oklch(0.75 0.20 140)',
];

interface DropRippleLoaderProps {
  /** Dots per row/column. Default 10 — fills wider containers without looking tiny. */
  size?: number;
  /** Dot diameter in px. Default 6. */
  dotSize?: number;
  /** Optional label rendered below the grid. */
  label?: string;
  className?: string;
}

export function DropRippleLoader({
  size = 10,
  dotSize = 6,
  label = 'Generating…',
  className,
}: DropRippleLoaderProps) {
  const centerR = (size - 1) / 2;
  const centerC = (size - 1) / 2;
  const maxDist = Math.sqrt(centerR * centerR + centerC * centerC) || 1;

  const dots = [];
  for (let i = 0; i < size * size; i++) {
    const row = Math.floor(i / size);
    const col = i % size;
    const dist = Math.sqrt((row - centerR) ** 2 + (col - centerC) ** 2);
    const normDist = dist / maxDist;
    const delay = (normDist * 0.9).toFixed(3);
    const color = PALETTE[(row + col) % PALETTE.length];

    dots.push(
      <span
        key={i}
        className="block rounded-full justify-self-center self-center"
        style={{
          width: dotSize,
          height: dotSize,
          backgroundColor: color,
          animation: 'drop-ripple 2.4s ease-in-out infinite',
          animationDelay: `${delay}s`,
          willChange: 'transform, opacity',
        }}
      />
    );
  }

  return (
    <div
      className={cn(
        'relative flex h-full w-full flex-1 flex-col items-stretch justify-center gap-3 p-4',
        className,
      )}
    >
      <div
        className="flex-1 grid w-full"
        style={{
          gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${size}, minmax(0, 1fr))`,
          animation: 'drop-ripple-breath 3.6s ease-in-out infinite',
        }}
      >
        {dots}
      </div>
      {label ? (
        <span className="text-center text-xs text-muted-foreground animate-pulse">
          {label}
        </span>
      ) : null}
    </div>
  );
}
